import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ShoppingCart, IndianRupee, Truck, Users,
  ChevronDown, ChevronRight, CalendarDays, Loader2, X, ChevronLeft, Activity,
  Plus, Trash2
} from "lucide-react";
import { format, parseISO } from "date-fns";
import CustomerActivityLog from "@/components/CustomerActivityLog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function formatINR(v) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);
}

function getOrderValue(order) {
  const pt = (order.payment_terms || '').toUpperCase();
  if (['ADV', 'DDAP', 'DDAP RECVD'].includes(pt) && order.total_discount > 0)
    return (order.total_value || 0) - (order.total_discount || 0);
  return order.total_value || order.total_price || 0;
}

const NINE_KG = new Set(['JH', 'SJH', 'JJH', 'W150', 'W180', '180', 'A180', 'PG']);
let _packSizesCache = {};
function getPackingKg(item) {
  const grade = (item.grade || '').toUpperCase().trim();
  const sku = (item.sku || item.packing || 'TIN').toUpperCase().trim();
  if (_packSizesCache[`${grade}|${sku}`]) return _packSizesCache[`${grade}|${sku}`];
  if (_packSizesCache[grade]) return _packSizesCache[grade];
  return NINE_KG.has(grade) ? 9 : 10;
}
function isExcludedFromAvrg(item) {
  const sku = (item.sku || item.packing || '').toUpperCase().replace(/ /g, '_');
  return sku.startsWith('VACUUM') || sku === 'TIN_REJ';
}
function getOrderAvrgExclVac(order) {
  const allItems = order?.items || [];
  const items = allItems.filter(i => !isExcludedFromAvrg(i));
  const kgs = items.reduce((s, i) => s + ((i.qty || 0) * getPackingKg(i)), 0);
  if (kgs <= 0) return 0;
  // Calculate value from filtered items only (exclude TIN_REJ, VACUUM)
  const filteredVal = items.reduce((s, i) => s + ((i.qty || 0) * (i.rate || 0)), 0);
  // If discount exists, apply proportionally to filtered portion
  if (order.total_discount > 0 && filteredVal > 0) {
    const allVal = allItems.reduce((s, i) => s + ((i.qty || 0) * (i.rate || 0)), 0);
    if (allVal > 0) {
      const discountShare = order.total_discount * (filteredVal / allVal);
      return (filteredVal - discountShare) / kgs;
    }
  }
  return filteredVal / kgs;
}
function getOrdersAvrg(orders) {
  let totalKgs = 0, totalVal = 0;
  orders.forEach(o => {
    const allItems = o?.items || [];
    const items = allItems.filter(i => !isExcludedFromAvrg(i));
    const kgs = items.reduce((s, i) => s + ((i.qty || 0) * getPackingKg(i)), 0);
    if (kgs <= 0) return;
    totalKgs += kgs;
    // Calculate value from filtered items only (exclude TIN_REJ, VACUUM)
    const filteredVal = items.reduce((s, i) => s + ((i.qty || 0) * (i.rate || 0)), 0);
    if (o.total_discount > 0 && filteredVal > 0) {
      const allVal = allItems.reduce((s, i) => s + ((i.qty || 0) * (i.rate || 0)), 0);
      if (allVal > 0) {
        const discountShare = o.total_discount * (filteredVal / allVal);
        totalVal += filteredVal - discountShare;
      } else {
        totalVal += filteredVal;
      }
    } else {
      totalVal += filteredVal;
    }
  });
  return totalKgs > 0 ? totalVal / totalKgs : 0;
}

function getStatusColor(status) {
  const m = { pending: 'bg-yellow-100 text-yellow-800', confirmed: 'bg-green-100 text-green-800', dispatched: 'bg-blue-100 text-blue-800', to_sort: 'bg-orange-100 text-orange-800', ready_to_dispatch: 'bg-teal-100 text-teal-800', cancelled: 'bg-red-100 text-red-800' };
  return m[status] || 'bg-gray-100 text-gray-800';
}

// ── Day section (orders / dispatch) ──
function DaySection({ date, orders, mode, onSelectOrder, onCustomerClick }) {
  const [open, setOpen] = useState(false);
  const dayStr = format(date, 'dd-MMM-yyyy');
  const totalQty = orders.reduce((s, o) => s + (o.total_qty || 0), 0);
  const totalValue = orders.reduce((s, o) => s + getOrderValue(o), 0);
  const avgRate = Math.round(getOrdersAvrg(orders));

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <div className="grid items-center px-3 py-1.5 bg-slate-50 hover:bg-slate-100 cursor-pointer border-b transition-colors" style={{gridTemplateColumns: '16px 100px 32px 80px 130px 90px'}} data-testid={`day-${format(date, 'yyyy-MM-dd')}`}>
          {open ? <ChevronDown className="w-3 h-3 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 text-muted-foreground" />}
          <span className="text-xs font-semibold">{dayStr}</span>
          <Badge variant="secondary" className="text-[9px] px-1 py-0 justify-self-center">{orders.length}</Badge>
          <span className="text-[10px] text-muted-foreground">Qty: <b className="text-foreground">{totalQty}</b></span>
          <span className="text-[10px] text-muted-foreground">Value: <b className="text-emerald-700">{formatINR(totalValue)}</b></span>
          {avgRate > 0 && <span className="text-[10px] text-muted-foreground">Avg: <b className="text-red-600">{formatINR(avgRate)}</b></span>}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <table className="text-[10px]" style={{width: 'auto'}}>
          <thead>
            <tr className="text-[9px] bg-muted/40 border-b">
              <th className="py-1 px-1.5 text-left font-medium" style={{width: '60px'}}>Order#</th>
              {mode === 'dispatch' && <th className="py-1 px-1.5 text-left font-medium" style={{width: '60px'}}>Dispatch#</th>}
              <th className="py-1 px-1.5 text-left font-medium" style={{width: '160px'}}>Customer</th>
              <th className="py-1 px-1.5 text-left font-medium" style={{width: '35px'}}>Qty</th>
              <th className="py-1 px-1.5 text-left font-medium" style={{width: '80px'}}>Value</th>
              <th className="py-1 px-1.5 text-left font-medium" style={{width: '65px'}}>Avg Rate</th>
              {mode === 'dispatch' && <th className="py-1 px-1.5 text-left font-medium" style={{width: '90px'}}>Transport</th>}
              {mode === 'order' && <th className="py-1 px-1.5 text-left font-medium" style={{width: '70px'}}>Status</th>}
            </tr>
          </thead>
          <tbody>
            {orders.map(o => (
              <tr key={o.id} className="border-b hover:bg-emerald-50 cursor-pointer transition-colors" onClick={() => onSelectOrder(o)} data-testid={`order-row-${o.id}`}>
                <td className="py-1 px-1.5 text-left font-mono font-semibold">{o.order_number}</td>
                {mode === 'dispatch' && <td className="py-1 px-1.5 text-left font-mono">{o.dispatch_number || '-'}</td>}
                <td className="py-1 px-1.5 text-left truncate" style={{maxWidth: '160px'}}>
                  <button className="text-left hover:text-blue-600 hover:underline transition-colors truncate w-full" onClick={(e) => { e.stopPropagation(); onCustomerClick?.(o.customer_name || o.dispatch_name); }}>
                    {o.dispatch_name || o.customer_name}
                  </button>
                </td>
                <td className="py-1 px-1.5 text-left">{o.total_qty || 0}</td>
                <td className="py-1 px-1.5 text-left font-semibold">{formatINR(getOrderValue(o))}</td>
                <td className="py-1 px-1.5 text-left text-red-600 font-medium">{Math.round(getOrderAvrgExclVac(o)) > 0 ? formatINR(Math.round(getOrderAvrgExclVac(o))) : '-'}</td>
                {mode === 'dispatch' && <td className="py-1 px-1.5 text-left truncate" style={{maxWidth: '90px'}}>{o.transport_name || '-'}</td>}
                {mode === 'order' && (
                  <td className="py-1 px-1.5 text-left">
                    <Badge variant="outline" className={`text-[9px] px-1 py-0 ${getStatusColor(o.status)}`}>{o.status}</Badge>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ── Month section (orders / dispatch) ──
function MonthSection({ monthKey, days, mode, onSelectOrder, onCustomerClick }) {
  const [open, setOpen] = useState(monthKey === format(new Date(), 'yyyy-MM'));
  const allOrders = days.flatMap(d => d.orders);
  const totalQty = allOrders.reduce((s, o) => s + (o.total_qty || 0), 0);
  const totalValue = allOrders.reduce((s, o) => s + getOrderValue(o), 0);
  const avgRate = Math.round(getOrdersAvrg(allOrders));
  const label = format(parseISO(monthKey + '-01'), 'MMMM yyyy');
  const avrgLabel = mode === 'dispatch' ? 'Dis AVRG' : 'Ord AVRG';

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <div className="grid items-center px-3 py-2 bg-emerald-50 hover:bg-emerald-100 cursor-pointer border-b border-emerald-200 transition-colors" style={{gridTemplateColumns: '20px 20px 120px 40px 80px 130px 100px'}} data-testid={`month-${monthKey}`}>
          {open ? <ChevronDown className="w-3.5 h-3.5 text-emerald-700" /> : <ChevronRight className="w-3.5 h-3.5 text-emerald-700" />}
          <CalendarDays className="w-3.5 h-3.5 text-emerald-600" />
          <span className="text-sm font-bold text-emerald-900">{label}</span>
          <Badge className="bg-emerald-700 text-white text-[10px] px-1.5 py-0 justify-self-center">{allOrders.length}</Badge>
          <span className="text-xs text-muted-foreground">Qty: <b className="text-foreground">{totalQty}</b></span>
          <span className="text-xs text-muted-foreground">Value: <b className="text-emerald-700">{formatINR(totalValue)}</b></span>
          {avgRate > 0 && <span className="text-xs text-muted-foreground">{avrgLabel}: <b className="text-red-600">{formatINR(avgRate)}</b></span>}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border-l-2 border-emerald-200 ml-2">
          {days.map(({ date, orders }) => (
            <DaySection key={date.toISOString()} date={date} orders={orders} mode={mode} onSelectOrder={onSelectOrder} onCustomerClick={onCustomerClick} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ── Receipt Day section ──
function ReceiptDaySection({ date, receipts }) {
  const [open, setOpen] = useState(false);
  const dayStr = format(date, 'dd-MMM-yyyy');
  const totalAmt = receipts.reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <div className="grid items-center px-3 py-1.5 bg-slate-50 hover:bg-slate-100 cursor-pointer border-b transition-colors" style={{gridTemplateColumns: '16px 100px 32px auto'}} data-testid={`rday-${format(date, 'yyyy-MM-dd')}`}>
          {open ? <ChevronDown className="w-3 h-3 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 text-muted-foreground" />}
          <span className="text-xs font-semibold">{dayStr}</span>
          <Badge variant="secondary" className="text-[9px] px-1 py-0 justify-self-center">{receipts.length}</Badge>
          <span className="text-[10px] text-muted-foreground">Amount: <b className="text-emerald-700">{formatINR(totalAmt)}</b></span>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <table className="text-[10px]" style={{width: 'auto'}}>
          <thead>
            <tr className="text-[9px] bg-muted/40 border-b">
              <th className="py-1 px-1.5 text-left font-medium" style={{width: '140px'}}>Credit (From)</th>
              <th className="py-1 px-1.5 text-left font-medium" style={{width: '110px'}}>Debit (To)</th>
              <th className="py-1 px-1.5 text-left font-medium" style={{width: '80px'}}>Amount</th>
              <th className="py-1 px-1.5 text-left font-medium" style={{width: '100px'}}>Reference</th>
              <th className="py-1 px-1.5 text-left font-medium" style={{width: '140px'}}>Narration</th>
            </tr>
          </thead>
          <tbody>
            {receipts.map(r => (
              <tr key={r.id} className="border-b hover:bg-amber-50 transition-colors">
                <td className="py-1 px-1.5 text-left truncate" style={{maxWidth: '140px'}}>{r.credit_account}</td>
                <td className="py-1 px-1.5 text-left truncate" style={{maxWidth: '110px'}}>{r.debit_account}</td>
                <td className="py-1 px-1.5 text-left font-semibold text-emerald-700">{formatINR(r.amount)}</td>
                <td className="py-1 px-1.5 text-left truncate" style={{maxWidth: '100px'}}>{r.reference || '-'}</td>
                <td className="py-1 px-1.5 text-left truncate" style={{maxWidth: '140px'}}>{r.narration || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ── Receipt Month section ──
function ReceiptMonthSection({ monthKey, days }) {
  const [open, setOpen] = useState(monthKey === format(new Date(), 'yyyy-MM'));
  const allReceipts = days.flatMap(d => d.receipts);
  const totalAmt = allReceipts.reduce((s, r) => s + (r.amount || 0), 0);
  const label = format(parseISO(monthKey + '-01'), 'MMMM yyyy');

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <div className="grid items-center px-3 py-2 bg-amber-50 hover:bg-amber-100 cursor-pointer border-b border-amber-200 transition-colors" style={{gridTemplateColumns: '20px 20px 120px 40px auto'}} data-testid={`rmonth-${monthKey}`}>
          {open ? <ChevronDown className="w-3.5 h-3.5 text-amber-700" /> : <ChevronRight className="w-3.5 h-3.5 text-amber-700" />}
          <IndianRupee className="w-3.5 h-3.5 text-amber-600" />
          <span className="text-sm font-bold text-amber-900">{label}</span>
          <Badge className="bg-amber-700 text-white text-[10px] px-1.5 py-0 justify-self-center">{allReceipts.length}</Badge>
          <span className="text-xs text-muted-foreground">Amount: <b className="text-emerald-700">{formatINR(totalAmt)}</b></span>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border-l-2 border-amber-200 ml-2">
          {days.map(({ date, receipts }) => (
            <ReceiptDaySection key={date.toISOString()} date={date} receipts={receipts} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ── Order detail side panel ──
function OrderDetailPanel({ order, onClose }) {
  if (!order) return null;
  const items = order.items || [];
  const totalQty = items.reduce((s, i) => s + (i.qty || 0), 0);

  return (
    <Card className="border-border/60 h-full overflow-hidden" data-testid="order-detail-panel">
      <div className="bg-gradient-to-r from-emerald-700 to-emerald-600 px-4 py-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base font-mono text-white">{order.order_number}</h3>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-white/80 hover:text-white hover:bg-white/20" onClick={onClose} data-testid="close-detail">
            <X className="w-4 h-4" />
          </Button>
        </div>
        <Badge className={`text-[10px] px-2 py-0.5 mt-1 ${getStatusColor(order.status)}`}>{order.status}</Badge>
      </div>
      <CardContent className="p-3">
        <div className="bg-slate-50 rounded-md p-2.5 mb-3">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Order Info</p>
          <div className="space-y-1.5 text-xs">
            <div className="grid grid-cols-2 gap-1">
              <span className="text-muted-foreground">Customer</span>
              <span className="font-medium text-right">{order.dispatch_name || order.customer_name}</span>
            </div>
            {order.order_date && <div className="grid grid-cols-2 gap-1"><span className="text-muted-foreground">Order Date</span><span className="font-medium text-right">{order.order_date}</span></div>}
            {order.dispatch_number && <div className="grid grid-cols-2 gap-1"><span className="text-muted-foreground">Dispatch No</span><span className="font-medium text-right font-mono">{order.dispatch_number}</span></div>}
            {order.payment_terms && <div className="grid grid-cols-2 gap-1"><span className="text-muted-foreground">Payment Terms</span><span className="font-medium text-right">{order.payment_terms}</span></div>}
            {order.transport_name && <div className="grid grid-cols-2 gap-1"><span className="text-muted-foreground">Transport</span><span className="font-medium text-right">{order.transport_name}</span></div>}
            {order.vehicle_number && <div className="grid grid-cols-2 gap-1"><span className="text-muted-foreground">Vehicle No</span><span className="font-medium text-right">{order.vehicle_number}</span></div>}
            {order.lr_number && <div className="grid grid-cols-2 gap-1"><span className="text-muted-foreground">LR No</span><span className="font-medium text-right">{order.lr_number}</span></div>}
            {order.invoice_number && <div className="grid grid-cols-2 gap-1"><span className="text-muted-foreground">Invoice No</span><span className="font-medium text-right">{order.invoice_number}</span></div>}
          </div>
        </div>
        {items.length > 0 && (
          <div className="mb-3">
            <div className="bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-t-md">Items</div>
            <table className="w-full text-[10px] border border-t-0 rounded-b-md overflow-hidden">
              <thead><tr className="bg-amber-50 border-b">
                <th className="py-1 px-1.5 text-center font-semibold">Grade</th>
                <th className="py-1 px-1.5 text-center font-semibold">SKU</th>
                <th className="py-1 px-1.5 text-center font-semibold">Qty</th>
                <th className="py-1 px-1.5 text-center font-semibold">Rate</th>
                <th className="py-1 px-1.5 text-center font-semibold">Amount</th>
              </tr></thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} className="border-b last:border-0">
                    <td className="py-1 px-1.5 text-center font-medium">{item.grade}</td>
                    <td className="py-1 px-1.5 text-center">{item.sku || item.packing || 'TIN'}</td>
                    <td className="py-1 px-1.5 text-center">{item.qty}</td>
                    <td className="py-1 px-1.5 text-center">{formatINR(item.rate)}</td>
                    <td className="py-1 px-1.5 text-center font-semibold">{formatINR(item.amount || item.qty * item.rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="bg-emerald-50 rounded-md p-2.5 space-y-1">
          <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-1">Summary</p>
          <div className="flex justify-between text-xs"><span className="text-muted-foreground">Total Qty</span><span className="font-bold">{totalQty}</span></div>
          <div className="flex justify-between text-xs"><span className="text-muted-foreground">Total Value</span><span className="font-bold text-emerald-700">{formatINR(order.total_value || order.total_price || 0)}</span></div>
          {order.total_discount > 0 && <div className="flex justify-between text-xs"><span className="text-muted-foreground">Discount</span><span className="text-red-600">-{formatINR(order.total_discount)}</span></div>}
          {order.total_discount > 0 && <div className="flex justify-between text-xs font-bold"><span className="text-muted-foreground">Invoice Value</span><span className="text-emerald-700">{formatINR((order.total_value || 0) - (order.total_discount || 0))}</span></div>}
        </div>
        {order.notes && <div className="mt-3 border-t pt-2"><p className="text-[10px] font-semibold text-muted-foreground">NOTES</p><p className="text-xs mt-0.5">{order.notes}</p></div>}
      </CardContent>
    </Card>
  );
}

// ── Customer Sales Data Table (FY wise) ──
function CustomerSalesTable({ fyYear, onChangeFY, onCustomerClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/customer-sales-data?fy_start=${fyYear}`)
      .then(res => setData(res.data))
      .catch((err) => { setData(null); })
      .finally(() => setLoading(false));
  }, [fyYear]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  if (!data || !data.months) return <p className="text-sm text-muted-foreground text-center py-8">Failed to load data. Try again.</p>;

  const { months, rows, header } = data;

  return (
    <div data-testid="customer-sales-table">
      {/* FY navigation */}
      <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border-b border-blue-200">
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onChangeFY(fyYear - 1)} data-testid="fy-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="text-sm font-bold text-blue-900">FY {fyYear}-{String(fyYear + 1).slice(2)}</span>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onChangeFY(fyYear + 1)} data-testid="fy-next"><ChevronRight className="w-4 h-4" /></Button>
        <span className="text-[10px] text-muted-foreground ml-2">Deal Customers: <b>{header.total_deal_customers}</b></span>
        <span className="text-[10px] text-muted-foreground ml-2">Total: <b className="text-emerald-700">{formatINR(header.grand_total)}</b></span>
      </div>

      {/* Scrollable table */}
      <div className="overflow-x-auto">
        <table className="text-[10px] w-full border-collapse">
          <thead className="sticky top-0 z-10">
            {/* Deal customer count row */}
            <tr className="bg-amber-50 border-b">
              <th className="py-1 px-1 text-center font-bold sticky left-0 bg-amber-50 z-20 border-r" style={{width: '28px', minWidth: '28px'}}>#</th>
              <th className="py-1 px-1.5 text-left font-bold sticky left-[28px] bg-amber-50 z-20 border-r" style={{width: '130px', minWidth: '130px'}}>DEAL CUSTOMER</th>
              <th className="py-1 px-1 text-center font-bold bg-amber-50 border-r" style={{width: '70px', minWidth: '70px'}}>TOTAL</th>
              {months.map(m => (
                <th key={m.key} className="py-1 px-1 text-center font-semibold text-amber-800 border-r" style={{width: '68px', minWidth: '68px'}}>
                  {header.deal_customers[m.key] || 0}
                </th>
              ))}
            </tr>
            {/* Monthly totals row */}
            <tr className="bg-emerald-50 border-b">
              <th className="py-1 px-1 sticky left-0 bg-emerald-50 z-20 border-r"></th>
              <th className="py-1 px-1.5 text-left font-bold sticky left-[28px] bg-emerald-50 z-20 border-r text-emerald-800">TOTAL SALES</th>
              <th className="py-1 px-1 text-center font-bold bg-emerald-50 border-r text-emerald-800">{formatINR(header.grand_total)}</th>
              {months.map(m => (
                <th key={m.key} className="py-1 px-1 text-center font-semibold text-emerald-700 border-r" style={{fontSize: '9px'}}>
                  {header.month_totals[m.key] ? formatINR(header.month_totals[m.key]) : '0'}
                </th>
              ))}
            </tr>
            {/* Sales AVRG row */}
            <tr className="bg-red-50 border-b">
              <th className="py-1 px-1 sticky left-0 bg-red-50 z-20 border-r"></th>
              <th className="py-1 px-1.5 text-left font-bold sticky left-[28px] bg-red-50 z-20 border-r text-red-700">SALES AVRG</th>
              <th className="py-1 px-1 text-center font-bold bg-red-50 border-r text-red-700">{header.total_sales_avrg ? formatINR(header.total_sales_avrg) : '0'}</th>
              {months.map(m => (
                <th key={m.key} className="py-1 px-1 text-center font-semibold text-red-600 border-r" style={{fontSize: '9px'}}>
                  {header.sales_avrg?.[m.key] ? formatINR(header.sales_avrg[m.key]) : '0'}
                </th>
              ))}
            </tr>
            {/* Column headers */}
            <tr className="bg-blue-100 border-b">
              <th className="py-1 px-1 text-center font-bold sticky left-0 bg-blue-100 z-20 border-r">SR</th>
              <th className="py-1 px-1.5 text-left font-bold sticky left-[28px] bg-blue-100 z-20 border-r">PARTY NAME</th>
              <th className="py-1 px-1 text-center font-bold bg-blue-100 border-r">TOTAL</th>
              {months.map(m => (
                <th key={m.key} className="py-1 px-1 text-center font-bold text-blue-900 border-r">{m.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.name} className="border-b hover:bg-blue-50/30 transition-colors">
                <td className="py-1 px-1 text-center font-mono sticky left-0 bg-white z-10 border-r">{row.sr}</td>
                <td className="py-1 px-1.5 text-left font-medium truncate sticky left-[28px] bg-white z-10 border-r" style={{maxWidth: '130px'}} title={row.name}>
                  <button className="text-left hover:text-blue-600 hover:underline transition-colors truncate w-full" onClick={() => onCustomerClick?.(row.name)}>{row.name}</button>
                </td>
                <td className="py-1 px-1 text-center font-bold text-emerald-700 border-r">{row.total > 0 ? formatINR(row.total) : '0'}</td>
                {months.map(m => {
                  const val = row.months[m.key] || 0;
                  return (
                    <td key={m.key} className={`py-1 px-1 text-center border-r ${val > 0 ? 'font-medium' : 'text-muted-foreground/40'}`}>
                      {val > 0 ? formatINR(val) : '0'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════ MAIN DASHBOARD ═══════
export default function Dashboard() {
  const [orders, setOrders] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dispatch');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [behaviourProfile, setBehaviourProfile] = useState(null);
  const [behaviourLoading, setBehaviourLoading] = useState(false);
  const [dashProducts, setDashProducts] = useState([]);
  const [fyYear, setFyYear] = useState(() => {
    const now = new Date();
    return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ordersRes, receiptsRes, packRes] = await Promise.all([
          axios.get(`${API}/orders`),
          axios.get(`${API}/receipts`),
          axios.get(`${API}/products/pack-sizes`)
        ]);
        setOrders(ordersRes.data);
        setReceipts(receiptsRes.data || []);
        _packSizesCache = packRes.data || {};
      } catch (e) { /* handled */ }
      finally { setLoading(false); }
    };
    fetchData();
    axios.get(`${API}/products`).then(r => setDashProducts(r.data || [])).catch(() => {});
  }, []);

  // Filter orders based on tab
  const filtered = useMemo(() => {
    if (activeTab === 'dispatch') return orders.filter(o => o.status === 'dispatched');
    if (activeTab === 'order') return orders;
    return [];
  }, [orders, activeTab]);

  const openBehaviourProfile = async (customerName) => {
    setBehaviourLoading(true);
    try {
      const res = await axios.get(`${API}/customer/behaviour-profile?customer_name=${encodeURIComponent(customerName)}`);
      setBehaviourProfile(res.data);
    } catch { setBehaviourProfile({ customer_name: customerName, stats: {}, rate_preference: '', tags: [], notes: '', bill_type: '', payment_terms: '', show_highest_selling: false, show_discounted_grades: false, discounted_grades: [] }); }
    finally { setBehaviourLoading(false); }
  };
  const saveBehaviourProfile = async () => {
    if (!behaviourProfile) return;
    try {
      await axios.put(`${API}/customer/behaviour-profile`, behaviourProfile);
    } catch {}
  };


  // Group orders by month → day — ASCENDING
  const monthGroups = useMemo(() => {
    if (activeTab === 'receipts') return [];
    const dateField = activeTab === 'dispatch' ? 'dispatched_at' : 'order_date';
    const groups = {};

    filtered.forEach(o => {
      let dateStr = o[dateField] || o.order_date || o.created_at;
      if (!dateStr) return;
      let date;
      try { date = new Date(dateStr); if (isNaN(date)) return; } catch { return; }
      const monthKey = format(date, 'yyyy-MM');
      const dayKey = format(date, 'yyyy-MM-dd');
      if (!groups[monthKey]) groups[monthKey] = {};
      if (!groups[monthKey][dayKey]) groups[monthKey][dayKey] = { date, orders: [] };
      groups[monthKey][dayKey].orders.push(o);
    });

    return Object.keys(groups)
      .sort((a, b) => a.localeCompare(b))
      .map(monthKey => ({
        monthKey,
        days: Object.values(groups[monthKey]).sort((a, b) => a.date - b.date)
      }));
  }, [filtered, activeTab]);

  // Group receipts by month → day — ASCENDING
  const receiptGroups = useMemo(() => {
    if (activeTab !== 'receipts') return [];
    const groups = {};

    receipts.forEach(r => {
      let dateStr = r.date || r.created_at;
      if (!dateStr) return;
      let date;
      try { date = new Date(dateStr); if (isNaN(date)) return; } catch { return; }
      const monthKey = format(date, 'yyyy-MM');
      const dayKey = format(date, 'yyyy-MM-dd');
      if (!groups[monthKey]) groups[monthKey] = {};
      if (!groups[monthKey][dayKey]) groups[monthKey][dayKey] = { date, receipts: [] };
      groups[monthKey][dayKey].receipts.push(r);
    });

    return Object.keys(groups)
      .sort((a, b) => a.localeCompare(b))
      .map(monthKey => ({
        monthKey,
        days: Object.values(groups[monthKey]).sort((a, b) => a.date - b.date)
      }));
  }, [receipts, activeTab]);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" data-testid="dashboard-page">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-foreground">Dashboard</h1>
        <p className="text-xs text-muted-foreground">Purelife Bot B2B Sales Portal</p>
      </div>

      {/* Simple Tabs: Dispatch | Order | Receipts */}
      <div className="flex items-center gap-1 mb-3 bg-muted rounded-lg p-0.5 w-fit" data-testid="dashboard-tabs">
        <Button variant={activeTab === 'dispatch' ? 'default' : 'ghost'} size="sm" className="h-8 px-4 text-xs"
          onClick={() => { setActiveTab('dispatch'); setSelectedOrder(null); }} data-testid="tab-dispatch">
          <Truck className="w-3.5 h-3.5 mr-1.5" /> Dispatch
        </Button>
        <Button variant={activeTab === 'order' ? 'default' : 'ghost'} size="sm" className="h-8 px-4 text-xs"
          onClick={() => { setActiveTab('order'); setSelectedOrder(null); }} data-testid="tab-order">
          <ShoppingCart className="w-3.5 h-3.5 mr-1.5" /> Order
        </Button>
        <Button variant={activeTab === 'receipts' ? 'default' : 'ghost'} size="sm" className="h-8 px-4 text-xs"
          onClick={() => { setActiveTab('receipts'); setSelectedOrder(null); }} data-testid="tab-receipts">
          <IndianRupee className="w-3.5 h-3.5 mr-1.5" /> Receipts
        </Button>
        <Button variant={activeTab === 'customer' ? 'default' : 'ghost'} size="sm" className="h-8 px-4 text-xs"
          onClick={() => { setActiveTab('customer'); setSelectedOrder(null); }} data-testid="tab-customer">
          <Users className="w-3.5 h-3.5 mr-1.5" /> Customer Data
        </Button>
        <Button variant={activeTab === 'activity' ? 'default' : 'ghost'} size="sm" className="h-8 px-4 text-xs"
          onClick={() => { setActiveTab('activity'); setSelectedOrder(null); }} data-testid="tab-activity">
          <Activity className="w-3.5 h-3.5 mr-1.5" /> Activity Log
        </Button>
      </div>

      {/* Main content */}
      {activeTab === 'customer' ? (
        <Card className="border-border/60" data-testid="customer-data-card">
          <CardContent className="p-0">
            <CustomerSalesTable fyYear={fyYear} onChangeFY={setFyYear} onCustomerClick={openBehaviourProfile} />
          </CardContent>
        </Card>
      ) : activeTab === 'activity' ? (
        <Card className="border-border/60" data-testid="activity-log-card">
          <CardContent className="p-4">
            <CustomerActivityLog />
          </CardContent>
        </Card>
      ) : (
      <div className={`grid gap-4 ${selectedOrder ? 'grid-cols-1 lg:grid-cols-5' : 'grid-cols-1'}`}>
        <Card className={`border-border/60 ${selectedOrder ? 'lg:col-span-3' : 'max-w-[65vw]'}`} data-testid="monthly-data">
          <CardContent className="p-0">
            <ScrollArea className={selectedOrder ? 'h-[calc(100vh-180px)]' : ''}>
              {/* Dispatch / Order view */}
              {(activeTab === 'dispatch' || activeTab === 'order') && (
                monthGroups.length > 0 ? (
                  monthGroups.map(({ monthKey, days }) => (
                    <MonthSection key={monthKey} monthKey={monthKey} days={days} mode={activeTab} onSelectOrder={setSelectedOrder} onCustomerClick={openBehaviourProfile} />
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Truck className="w-10 h-10 mb-2 opacity-40" />
                    <p className="text-sm">No {activeTab === 'dispatch' ? 'dispatched orders' : 'orders'} found</p>
                  </div>
                )
              )}

              {/* Receipts view */}
              {activeTab === 'receipts' && (
                receiptGroups.length > 0 ? (
                  receiptGroups.map(({ monthKey, days }) => (
                    <ReceiptMonthSection key={monthKey} monthKey={monthKey} days={days} />
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <IndianRupee className="w-10 h-10 mb-2 opacity-40" />
                    <p className="text-sm">No receipts found</p>
                  </div>
                )
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Order Detail Side Panel (only for dispatch/order tabs) */}
        {selectedOrder && (activeTab === 'dispatch' || activeTab === 'order') && (
          <div className="lg:col-span-2">
            <ScrollArea className="h-[calc(100vh-180px)]">
              <OrderDetailPanel order={selectedOrder} onClose={() => setSelectedOrder(null)} />
            </ScrollArea>
          </div>
        )}
      </div>
      )}

      {/* Customer Behaviour Profile Dialog */}
      <Dialog open={!!behaviourProfile} onOpenChange={(open) => { if (!open) { saveBehaviourProfile(); setBehaviourProfile(null); } }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">Customer Profile — {behaviourProfile?.customer_name}</DialogTitle>
          </DialogHeader>
          {behaviourProfile && (() => {
            const s = behaviourProfile.stats || {};
            const TAGS = ["Quick Closer", "Hard Negotiator", "Price Sensitive", "Volume Buyer", "Seasonal Buyer", "Loyal Customer"];
            return (
              <div className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-blue-50 rounded-lg p-2 text-center">
                    <p className="text-[8px] text-muted-foreground">Deals</p>
                    <p className="text-sm font-bold text-blue-800">{s.total_deals || 0}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-2 text-center">
                    <p className="text-[8px] text-muted-foreground">Accept %</p>
                    <p className="text-sm font-bold text-green-800">{s.acceptance_rate || 0}%</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2 text-center">
                    <p className="text-[8px] text-muted-foreground">Avg Rounds</p>
                    <p className="text-sm font-bold text-amber-800">{s.avg_rounds || 0}</p>
                  </div>
                </div>

                {/* Rate Preference */}
                <div>
                  <p className="text-[10px] font-bold text-muted-foreground mb-1">Rate Preference (auto-applied to customer)</p>
                  <div className="flex gap-1.5">
                    {['CR', 'BSR-1', 'BSR-2', 'BSR-3'].map(pref => (
                      <button key={pref} onClick={() => setBehaviourProfile(prev => ({...prev, rate_preference: pref}))}
                        className={`text-[10px] font-bold px-3 py-1.5 rounded-lg border transition-colors ${behaviourProfile.rate_preference === pref ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                        {pref}
                      </button>
                    ))}
                  </div>
                  {behaviourProfile.rate_preference && <p className="text-[9px] text-emerald-600 mt-1">Customer will see {behaviourProfile.rate_preference} rates by default</p>}
                </div>

                {/* Customer Display Preferences */}
                <div className="border rounded-lg p-3 space-y-3 bg-gray-50/50">
                  <p className="text-[10px] font-bold text-muted-foreground">Customer New Order Display</p>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!behaviourProfile.show_highest_selling}
                      onChange={e => setBehaviourProfile(prev => ({...prev, show_highest_selling: e.target.checked}))}
                      className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500" />
                    <span className="text-[11px] font-semibold text-gray-700">Show Highest Selling Grades</span>
                    <span className="text-[9px] text-muted-foreground ml-auto">Grade + SKU</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!behaviourProfile.show_discounted_grades}
                      onChange={e => setBehaviourProfile(prev => ({...prev, show_discounted_grades: e.target.checked}))}
                      className="w-4 h-4 rounded border-gray-300 text-rose-600 focus:ring-rose-500" />
                    <span className="text-[11px] font-semibold text-gray-700">Show Discounted Grades</span>
                  </label>

                  {behaviourProfile.show_discounted_grades && (
                    <div className="space-y-2 pl-1">
                      <p className="text-[9px] text-muted-foreground">Add grades with original & offered rates:</p>
                      {(behaviourProfile.discounted_grades || []).map((dg, idx) => (
                        <div key={idx} className="flex items-center gap-1.5">
                          <Select value={dg.grade || ''} onValueChange={v => {
                            const updated = [...(behaviourProfile.discounted_grades || [])];
                            updated[idx] = {...updated[idx], grade: v};
                            setBehaviourProfile(prev => ({...prev, discounted_grades: updated}));
                          }}>
                            <SelectTrigger className="h-7 text-[10px] w-[90px] px-1.5"><SelectValue placeholder="Grade" /></SelectTrigger>
                            <SelectContent className="max-h-[200px]">
                              {(dashProducts || []).map(p => p.grade).filter((v, i, a) => v && a.indexOf(v) === i).sort().map(g => (
                                <SelectItem key={g} value={g} className="text-xs">{g}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select value={dg.sku || 'BUCKET'} onValueChange={v => {
                            const updated = [...(behaviourProfile.discounted_grades || [])];
                            updated[idx] = {...updated[idx], sku: v};
                            setBehaviourProfile(prev => ({...prev, discounted_grades: updated}));
                          }}>
                            <SelectTrigger className="h-7 text-[10px] w-[72px] px-1.5"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="BUCKET" className="text-xs">BUCKET</SelectItem>
                              <SelectItem value="TIN" className="text-xs">TIN</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input type="number" value={dg.original_rate || ''} placeholder="Rate ₹"
                            onChange={e => {
                              const updated = [...(behaviourProfile.discounted_grades || [])];
                              updated[idx] = {...updated[idx], original_rate: e.target.value ? parseFloat(e.target.value) : ''};
                              setBehaviourProfile(prev => ({...prev, discounted_grades: updated}));
                            }}
                            className="h-7 text-[10px] w-[70px] px-1.5 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                          <span className="text-[10px] text-muted-foreground">→</span>
                          <Input type="number" value={dg.offered_rate || ''} placeholder="Offer ₹"
                            onChange={e => {
                              const updated = [...(behaviourProfile.discounted_grades || [])];
                              updated[idx] = {...updated[idx], offered_rate: e.target.value ? parseFloat(e.target.value) : ''};
                              setBehaviourProfile(prev => ({...prev, discounted_grades: updated}));
                            }}
                            className="h-7 text-[10px] w-[70px] px-1.5 text-center border-green-300 bg-green-50/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                          <button onClick={() => {
                            const updated = (behaviourProfile.discounted_grades || []).filter((_, i) => i !== idx);
                            setBehaviourProfile(prev => ({...prev, discounted_grades: updated}));
                          }} className="text-red-400 hover:text-red-600 p-0.5">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <button onClick={() => {
                        const updated = [...(behaviourProfile.discounted_grades || []), { grade: '', sku: 'BUCKET', original_rate: '', offered_rate: '' }];
                        setBehaviourProfile(prev => ({...prev, discounted_grades: updated}));
                      }} className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 font-semibold">
                        <Plus className="w-3 h-3" /> Add Grade
                      </button>
                    </div>
                  )}

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!behaviourProfile.combo_enabled}
                      onChange={e => setBehaviourProfile(prev => ({...prev, combo_enabled: e.target.checked}))}
                      className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      data-testid="combo-enabled-checkbox" />
                    <span className="text-[11px] font-semibold text-gray-700">Show Combo Offers</span>
                  </label>
                  {behaviourProfile.combo_enabled && (
                    <div className="grid grid-cols-2 gap-2 pl-1">
                      <div>
                        <p className="text-[9px] text-muted-foreground mb-0.5">Target Avg / Kg (dispatch avg)</p>
                        <Input type="number"
                          value={behaviourProfile.combo_target_avg ?? ''}
                          placeholder={behaviourProfile.default_month_dispatch_avg ? `₹${behaviourProfile.default_month_dispatch_avg}` : '₹ / kg'}
                          onChange={e => setBehaviourProfile(prev => ({...prev, combo_target_avg: e.target.value}))}
                          className="h-7 text-[10px] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          data-testid="combo-target-input" />
                      </div>
                      <div>
                        <p className="text-[9px] text-muted-foreground mb-0.5">Shuffles Allowed</p>
                        <Select value={String(behaviourProfile.combo_shuffle_limit || 2)}
                          onValueChange={v => setBehaviourProfile(prev => ({...prev, combo_shuffle_limit: parseInt(v)}))}>
                          <SelectTrigger className="h-7 text-[10px]" data-testid="combo-shuffle-limit"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2" className="text-xs">2 shuffles</SelectItem>
                            <SelectItem value="3" className="text-xs">3 shuffles</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                </div>

                {/* Bill Type */}
                <div>
                  <p className="text-[10px] font-bold text-muted-foreground mb-1">Default Bill Type</p>
                  <div className="flex gap-1.5">
                    {['EST', 'INV'].map(bt => (
                      <button key={bt} onClick={() => setBehaviourProfile(prev => ({...prev, bill_type: bt}))}
                        className={`text-[10px] font-bold px-3 py-1.5 rounded-lg border transition-colors ${behaviourProfile.bill_type === bt ? 'bg-amber-600 text-white border-amber-600' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                        {bt}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Payment Terms */}
                <div>
                  <p className="text-[10px] font-bold text-muted-foreground mb-1">Default Payment Terms</p>
                  <div className="flex gap-1.5 flex-wrap">
                    {['ADV', 'RDAP', 'DDAP', 'DDAP RECVD', 'REG'].map(pt => (
                      <button key={pt} onClick={() => setBehaviourProfile(prev => ({...prev, payment_terms: pt}))}
                        className={`text-[10px] font-bold px-2.5 py-1 rounded-lg border transition-colors ${behaviourProfile.payment_terms === pt ? 'bg-purple-600 text-white border-purple-600' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                        {pt}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Round Power */}
                <div>
                  <p className="text-[10px] font-bold text-muted-foreground mb-1">Negotiation Round Power</p>
                  <Input value={behaviourProfile.round_power || ''} onChange={e => setBehaviourProfile(prev => ({...prev, round_power: e.target.value}))}
                    placeholder="e.g., 2-3 rounds typical" className="h-8 text-xs" />
                </div>

                {/* Credit Limit */}
                <div>
                  <p className="text-[10px] font-bold text-muted-foreground mb-1">Credit Limit (₹)</p>
                  <Input type="number" value={behaviourProfile.credit_limit ?? 2000000}
                    onChange={e => setBehaviourProfile(prev => ({...prev, credit_limit: e.target.value ? parseFloat(e.target.value) : 0}))}
                    placeholder="Default: 20,00,000" className="h-8 text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                  <p className="text-[8px] text-muted-foreground mt-0.5">Default: ₹20,00,000</p>
                </div>

                {/* Tags */}
                <div>
                  <p className="text-[10px] font-bold text-muted-foreground mb-1">Behaviour Tags</p>
                  <div className="flex flex-wrap gap-1.5">
                    {TAGS.map(tag => (
                      <button key={tag} onClick={() => {
                        const tags = behaviourProfile.tags || [];
                        setBehaviourProfile(prev => ({...prev, tags: tags.includes(tag) ? tags.filter(t => t !== tag) : [...tags, tag]}));
                      }} className={`text-[9px] px-2 py-1 rounded-full border transition-colors ${(behaviourProfile.tags || []).includes(tag) ? 'bg-blue-100 border-blue-400 text-blue-800 font-semibold' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <p className="text-[10px] font-bold text-muted-foreground mb-1">Notes</p>
                  <Textarea value={behaviourProfile.notes || ''} onChange={e => setBehaviourProfile(prev => ({...prev, notes: e.target.value}))}
                    placeholder="Observations..." className="text-xs min-h-[40px]" />
                </div>

                {/* Suggestion */}
                {(behaviourProfile.rate_preference || behaviourProfile.show_highest_selling || behaviourProfile.show_discounted_grades) && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                    <p className="text-[9px] text-emerald-700">
                      {behaviourProfile.rate_preference && <>Auto-rate: <b>{behaviourProfile.rate_preference}</b> | </>}
                      Bill: <b>{behaviourProfile.bill_type || 'Not set'}</b> | Pay: <b>{behaviourProfile.payment_terms || 'Not set'}</b>
                      {behaviourProfile.show_highest_selling && <> | <b>Highest Selling</b> ✓</>}
                      {behaviourProfile.show_discounted_grades && <> | <b>Discounted</b> ({(behaviourProfile.discounted_grades || []).length})</>}
                    </p>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
